/**
 * RD-NOTICES-V2 — the registry `NoticeReceiptsSync` uses to route
 * `user_notice_receipts` rows to the right local store, and to collect each
 * store's local-only receipts for the one-time post-hydration upload.
 *
 * Each store file owns its OWN localStorage shape and merge rule (see each
 * file's header); this registry only knows how to recognize a notice_key and
 * which store's functions to call.
 */
import {
  ownsEstimateResponseNoticeKey,
  mergeEstimateResponseReceiptsFromServer,
  collectEstimateResponseReceiptsForUpload,
} from '../estimate/estimateResponseSummary';
import {
  ownsRouteChangeNoticeKey,
  mergeRouteChangeReceiptsFromServer,
  collectRouteChangeReceiptsForUpload,
} from '../worker/workerTransferNotice';
import {
  ownsMfaNudgeNoticeKey,
  mergeMfaNudgeSnoozeFromServer,
  collectMfaNudgeReceiptsForUpload,
} from '../auth/mfaNudgeSnooze';
import {
  ownsOwnerTransferNoticeKey,
  mergeOwnerTransferReceiptsFromServer,
  collectOwnerTransferReceiptsForUpload,
} from '../admin/ownerTransferNoticeStorage';
import {
  OFFICE_INBOX_NOTICE_KEY,
  mergeOfficeInboxSeenFromServer,
  collectOfficeInboxReceiptsForUpload,
} from '../office/officeInboxSeen';
import {
  ownsDeliveryIssueNoticeKey,
  mergeDeliveryIssueReceiptsFromServer,
  collectDeliveryIssueReceiptsForUpload,
} from '../delivery/deliveryIssueReceipts';
import {
  ownsSecurityAlertNoticeKey,
  mergeSecurityAlertReceiptsFromServer,
  collectSecurityAlertReceiptsForUpload,
} from './securityAlertReceipts';
import {
  ownsRoleWelcomeNoticeKey,
  mergeRoleWelcomeSeenFromServer,
  collectRoleWelcomeReceiptsForUpload,
} from '../onboarding/roleWelcomeSeen';
import {
  ownsCompanyAccessNoticeKey,
  mergeCompanyAccessGreetedFromServer,
  collectCompanyAccessGreetedForUpload,
} from '../company/companyAccessGreetingReceipts';
import {
  ownsSubscribeCelebrationNoticeKey,
  mergeSubscribeCelebratedFromServer,
  collectSubscribeCelebratedForUpload,
} from '../billing/subscribeCelebrationReceipts';
import {
  ownsTeammateJoinedNoticeKey,
  mergeTeammateJoinedFromServer,
  collectTeammateJoinedForUpload,
} from '../team/teammateJoinedReceipts';

function ownsOfficeInboxNoticeKey(noticeKey) {
  return noticeKey === OFFICE_INBOX_NOTICE_KEY;
}

/**
 * Each entry: `owns(noticeKey)` routes a fetched/realtime row here;
 * `merge(userId, tenantId, rows, storage)` applies them locally (monotonic —
 * see each store); `collect(userId, tenantId, storage)` snapshots this
 * store's local state for the one-time upload.
 */
const ADAPTERS = [
  {
    name: 'estimateResponse',
    owns: ownsEstimateResponseNoticeKey,
    merge: (userId, _tenantId, rows, storage) => mergeEstimateResponseReceiptsFromServer(userId, rows, storage),
    collect: (userId, _tenantId, storage) => collectEstimateResponseReceiptsForUpload(userId, storage),
  },
  {
    name: 'officeInbox',
    owns: ownsOfficeInboxNoticeKey,
    merge: (userId, tenantId, rows, storage) => {
      const row = rows.find((r) => r?.acked_at);
      if (row) mergeOfficeInboxSeenFromServer(userId, tenantId, row.acked_at, storage);
    },
    collect: (userId, tenantId, storage) => collectOfficeInboxReceiptsForUpload(userId, tenantId, storage),
  },
  {
    name: 'routeChange',
    owns: ownsRouteChangeNoticeKey,
    merge: (userId, _tenantId, rows, storage) => mergeRouteChangeReceiptsFromServer(userId, rows, storage),
    collect: (userId, _tenantId, storage) => collectRouteChangeReceiptsForUpload(userId, storage),
  },
  {
    name: 'mfaNudge',
    owns: ownsMfaNudgeNoticeKey,
    merge: (userId, _tenantId, rows) => {
      const row = rows.find((r) => r?.snoozed_until);
      if (row) mergeMfaNudgeSnoozeFromServer(userId, row.snoozed_until);
    },
    collect: (userId) => collectMfaNudgeReceiptsForUpload(userId),
  },
  {
    name: 'ownerTransfer',
    owns: ownsOwnerTransferNoticeKey,
    merge: (userId, _tenantId, rows, storage) => mergeOwnerTransferReceiptsFromServer(userId, rows, storage),
    collect: (userId, _tenantId, storage) => collectOwnerTransferReceiptsForUpload(userId, storage),
  },
  {
    name: 'deliveryIssue',
    owns: ownsDeliveryIssueNoticeKey,
    merge: (userId, _tenantId, rows, storage) => mergeDeliveryIssueReceiptsFromServer(userId, rows, storage),
    collect: (userId, _tenantId, storage) => collectDeliveryIssueReceiptsForUpload(userId, storage),
  },
  {
    name: 'securityAlert',
    owns: ownsSecurityAlertNoticeKey,
    merge: (userId, _tenantId, rows, storage) => mergeSecurityAlertReceiptsFromServer(userId, rows, storage),
    collect: (userId, _tenantId, storage) => collectSecurityAlertReceiptsForUpload(userId, storage),
  },
  {
    name: 'roleWelcome',
    owns: ownsRoleWelcomeNoticeKey,
    merge: (userId, tenantId, rows, storage) => {
      const row = rows.find((r) => r?.acked_at);
      if (row) mergeRoleWelcomeSeenFromServer(userId, tenantId, row.acked_at, storage);
    },
    collect: (userId, tenantId, storage) => collectRoleWelcomeReceiptsForUpload(userId, tenantId, storage),
  },
  {
    name: 'companyAccess',
    owns: ownsCompanyAccessNoticeKey,
    merge: (userId, tenantId, rows, storage) => mergeCompanyAccessGreetedFromServer(userId, tenantId, rows, storage),
    collect: (userId, tenantId, storage) => collectCompanyAccessGreetedForUpload(userId, tenantId, storage),
  },
  {
    name: 'subscribeCelebration',
    owns: ownsSubscribeCelebrationNoticeKey,
    merge: (userId, tenantId, rows, storage) => mergeSubscribeCelebratedFromServer(userId, tenantId, rows, storage),
    collect: (userId, tenantId, storage) => collectSubscribeCelebratedForUpload(userId, tenantId, storage),
  },
  {
    name: 'teammateJoined',
    owns: ownsTeammateJoinedNoticeKey,
    merge: (userId, tenantId, rows, storage) => mergeTeammateJoinedFromServer(userId, tenantId, rows, storage),
    collect: (userId, tenantId, storage) => collectTeammateJoinedForUpload(userId, tenantId, storage),
  },
];

function adapterFor(noticeKey) {
  return ADAPTERS.find((a) => a.owns(noticeKey));
}

/** Groups `rows` by adapter and applies each group. Rows matching no known
 *  adapter (e.g. a notice_key from a future phase) are silently ignored —
 *  forward compatibility, never a crash. */
export function applyNoticeReceiptsToLocalStores(userId, tenantId, rows, storage) {
  if (!userId || !Array.isArray(rows) || rows.length === 0) return;
  const grouped = new Map();
  rows.forEach((row) => {
    const adapter = adapterFor(row?.notice_key);
    if (!adapter) return;
    if (!grouped.has(adapter)) grouped.set(adapter, []);
    grouped.get(adapter).push(row);
  });
  grouped.forEach((groupRows, adapter) => {
    try {
      adapter.merge(userId, tenantId, groupRows, storage);
    } catch (e) {
      console.warn(`noticeReceiptAdapters: merge failed for ${adapter.name}`, e);
    }
  });
}

/** Every store's current local receipts, for the one-time post-hydration
 *  upload of local-only/newer-local data. */
export function collectAllLocalNoticeReceipts(userId, tenantId, storage) {
  if (!userId) return [];
  const rows = [];
  ADAPTERS.forEach((adapter) => {
    try {
      rows.push(...(adapter.collect(userId, tenantId, storage) || []));
    } catch (e) {
      console.warn(`noticeReceiptAdapters: collect failed for ${adapter.name}`, e);
    }
  });
  return rows;
}
